import { PropsWithChildren, useCallback, useMemo } from 'react';
import { Command as Cmdk } from 'cmdk';
import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigate } from 'react-router';

import {
  Badge,
  Dialog,
  ScrollArea,
  Text,
  useDialog,
} from '@components/primitives';

import { Command } from '@components/features/CommandMenu/types';

import { SelectEvent } from './events';

import css from './styles.module.css';

const commandContextOrder: Record<NonNullable<Command['context']>, number> = {
  global: 0,
  user: 1,
  team: 2,
  app: 3,
  page: 4,
  item: 5,
};

function filterCommands(commands: Command[], search: string) {
  const query = search.toLowerCase().trim();
  return commands.filter((command) => {
    const searchable = command.searchable ?? 'always';

    if (searchable === 'only' && query.length === 0) {
      return false;
    }

    if (searchable === 'never' && query.length > 0) {
      return false;
    }

    if (command.filterable === false) {
      return true;
    }

    const keywords = [
      command.group,
      command.label,
      ...(command.keywords ?? []),
    ];
    return keywords.some((keyword) => {
      if (typeof keyword === 'function') {
        return false;
      }

      return (keyword ?? '').toLowerCase().includes(query);
    });
  });
}

function sortCommands(commands: Command[]) {
  return commands.sort((a, b) => {
    const contextA = commandContextOrder[a.context ?? 'global'];
    const contextB = commandContextOrder[b.context ?? 'global'];
    const contextDelta = contextB - contextA;
    if (contextDelta !== 0) {
      return contextDelta;
    }

    const groupA = a.group ?? '';
    const groupB = b.group ?? '';
    if (groupA !== groupB) {
      return 0;
    }

    const priorityA = a.priority ?? 0;
    const priorityB = b.priority ?? 0;
    const priorityDelta = priorityA - priorityB;
    return priorityDelta;
  });
}

function groupCommands(commands: Command[], search: string) {
  const groups: { label: string; commands: Command[] }[] = [];

  for (const command of commands) {
    const label =
      typeof command.group === 'function'
        ? command.group(search)
        : (command.group ?? '');
    let group = groups.find((g) => g.label === label);
    if (!group) {
      group = { label, commands: [] };
      groups.push(group);
    }
    group.commands.push(command);
  }

  return groups.sort((a, b) => {
    const contextA = commandContextOrder[a.commands[0].context ?? 'global'];
    const contextB = commandContextOrder[b.commands[0].context ?? 'global'];
    const contextDelta = contextB - contextA;
    if (contextDelta !== 0) {
      return contextDelta;
    }

    const priorityA = a.commands[0].priority ?? 0;
    const priorityB = b.commands[0].priority ?? 0;
    const priorityDelta = priorityA - priorityB;
    return priorityDelta;
  });
}

interface CommandGroupProps extends PropsWithChildren {
  label: string;
}
function CommandGroup({ label, children }: CommandGroupProps) {
  return (
    <Cmdk.Group className={css.group}>
      {label && <p className={css.groupLabel}>{label}</p>}
      {children}
    </Cmdk.Group>
  );
}

interface CommandListProps {
  isLoading: boolean;
  search: string;
  onSearchChange(search: string): void;
  commands: Record<string, Command>;
  onSelect(): void;
}

function CommandList({
  isLoading,
  search,
  onSearchChange,
  commands,
  onSelect,
}: CommandListProps) {
  const navigate = useNavigate();
  const handleSelect = useCallback(
    (command: Command) => {
      if (command.type === 'route') {
        if (
          typeof command.href === 'string' &&
          command.href.startsWith('http')
        ) {
          window.open(command.href, '_blank');
        } else {
          void navigate(command.href);
        }
      }

      const event = new SelectEvent(search);
      command.onSelect?.(event, { navigate });

      if (!event.defaultPrevented) {
        onSelect();
      }
    },
    [search, navigate, onSelect]
  );

  const filteredCommands = useMemo(
    () => filterCommands(Object.values(commands), search),
    [commands, search]
  );
  const sortedCommands = useMemo(
    () => sortCommands(filteredCommands),
    [filteredCommands]
  );
  const groupedCommands = useMemo(
    () => groupCommands(sortedCommands, search),
    [sortedCommands, search]
  );

  return (
    <Cmdk shouldFilter={false} loop>
      <Cmdk.Input
        className={css.input}
        placeholder="Type a command or search..."
        value={search}
        onValueChange={onSearchChange}
      />

      {isLoading && <Cmdk.Loading className={css.loading} />}

      <ScrollArea style={{ height: 'auto', maxHeight: 375 }}>
        <Cmdk.List className={css.list}>
          {groupedCommands.map((group) => (
            <CommandGroup key={group.label} label={group.label}>
              {group.commands.map((command) => {
                const label =
                  typeof command.label === 'function'
                    ? command.label(search)
                    : command.label;
                return (
                  <Cmdk.Item
                    key={command.id}
                    className={css.item}
                    value={command.id}
                    onSelect={handleSelect.bind(null, command)}
                    data-variant={command.variant}
                  >
                    <span className={css.icon}>{command.icon}</span>
                    <span className={css.label}>
                      {label}
                      {command.sublabel && (
                        <span className={css.sublabel}>{command.sublabel}</span>
                      )}
                    </span>
                    {command.badge && (
                      <Badge size="sm" variant="muted" className={css.badge}>
                        {command.badge}
                      </Badge>
                    )}
                  </Cmdk.Item>
                );
              })}
            </CommandGroup>
          ))}

          <Cmdk.Empty className={css.empty}>
            <Text color="var(--text-muted)">No results found</Text>
          </Cmdk.Empty>
        </Cmdk.List>
      </ScrollArea>
    </Cmdk>
  );
}

export interface CommandMenuProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  search: string;
  onSearchChange(search: string): void;
  isLoading: boolean;
  commands: Record<string, Command>;
}

export function CommandMenu({
  open,
  onOpenChange,
  search,
  onSearchChange,
  isLoading,
  commands,
}: CommandMenuProps) {
  useHotkeys(['meta+k', '/'], () => onOpenChange(!open), {
    preventDefault: true,
  });

  const handleSelect = useCallback(() => {
    onSearchChange('');
    onOpenChange(false);
  }, []);

  const state = useDialog({ open, onOpenChange });

  return (
    <Dialog.Root state={state}>
      <Dialog.Overlay className={css.overlay} />
      <div className={css.content}>
        <Dialog.Content className={css.menu}>
          <Dialog.Title className="sr-only">Command Menu</Dialog.Title>
          <Dialog.Description className="sr-only">
            Search for a command to run
          </Dialog.Description>
          <CommandList
            isLoading={isLoading}
            search={search}
            onSearchChange={onSearchChange}
            commands={commands}
            onSelect={handleSelect}
          />
        </Dialog.Content>
      </div>
    </Dialog.Root>
  );
}
